/**
 * Styles para a area /clientes (CRM do cadastro de Cliente, backend-driven).
 *
 * Separado de `styles.jsx` (que pertence ao painel operacional baseado em
 * planilha) para evitar conflito de nomenclaturas. Mantem o look-and-feel
 * do design system Fluxo (cobre + grafite) sem decoracao supérflua, com
 * `table-layout: fixed` e ellipsis para garantir o principio constitucional
 * de ZERO scroll lateral.
 */
import styled from "styled-components";

import { getToneTokens } from "../../styles/shared";

export const SubNav = styled.nav`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  margin-bottom: 16px;
`;

export const SubNavLink = styled.button`
  background: none;
  border: 0;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  color: ${({ theme, $active }) => ($active ? theme.accent : theme.textSecondary)};
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.accent : "transparent")};
  margin-bottom: -1px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const Card = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const CardTitle = styled.h3`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  cursor: pointer;
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.accent};
  color: var(--ink-on-accent);
  border: 0;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.dangerText};
  border-color: ${({ theme }) => theme.dangerBorder};

  &:hover {
    background: ${({ theme }) => theme.dangerSurface};
  }
`;

/* ZERO scroll lateral: table-layout fixed + overflow-x hidden + ellipsis. */
export const TableWrapper = styled.div`
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  /* Bugfix #13: overflow-x clip (nao auto-promove overflow-y) */
  overflow-x: clip;
`;

export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.88rem;

  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.textSecondary};
    font-weight: 600;
    background: ${({ theme }) => theme.surfaceAlt};
  }

  tbody tr {
    cursor: ${({ $rowsClickable }) => ($rowsClickable ? "pointer" : "default")};
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ theme, $tone = "neutral" }) =>
    getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).text};
  border: 1px solid
    ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).border};
  white-space: nowrap;
`;

export const EmptyMsg = styled.p`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.88rem;
`;

export const ErrorMsg = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.dangerSurface};
  color: ${({ theme }) => theme.dangerText};
  border: 1px solid ${({ theme }) => theme.dangerBorder};
  font-size: 0.85rem;
`;

export const InfoMsg = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.infoSurface};
  color: ${({ theme }) => theme.infoText};
  border: 1px solid ${({ theme }) => theme.infoBorder};
  font-size: 0.85rem;
`;

/* --- Detalhe (ficha em 3 zonas) --- */

export const DetailBlocks = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const Block = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const BlockTitle = styled.h3`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  font-size: 0.88rem;
  align-items: baseline;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

export const FieldLabel = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FieldValue = styled.span`
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

/* --- Form (cadastro/edicao em SlideOverPanel) --- */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  > .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/** Grupo de campos com título próprio (ex.: a lista de endereços do form). */
export const FieldGroup = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

export const FieldGroupTitle = styled.legend`
  padding: 0;
  margin-bottom: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Input = styled.input`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -1px;
    border-color: transparent;
  }
`;

export const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.88rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

export const FormSelect = styled(Select)`
  font-size: 0.88rem;
`;

export const FormFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

export const InlineHint = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
`;
